// Reading signing participants without anyone's full national ID.
//
// Workflow reads carry each participant's national ID masked and `is_me`,
// computed by the server against the caller's own certificates. Who the
// caller IS comes only from `is_me`; the masked number only helps pick which
// of the caller's own certificates fits a slot (the server checks again when
// the signature arrives). A read without `is_me` (an older backend) matches
// by account alone.

import { maskNationalId, nationalIdsMayMatch } from '@/shared/utils/nationalId';
import { field } from './signingState';

/** The participant's national ID as read (masked by the server), or null. */
export const participantNationalId = (p) =>
  field(p, 'national_id_masked') || field(p, 'national_id_value') || null;

/** For display: always masked, whatever the read held. */
export const participantIdLabel = (p) => maskNationalId(participantNationalId(p));

/** This slot is the caller's. */
export function participantIsMe(p, { userId } = {}) {
  const flag = field(p, 'is_me');
  if (typeof flag === 'boolean') return flag;
  const uid = field(p, 'user_id');
  return uid != null && userId != null && String(uid) === String(userId);
}

/** A certificate's PINFL — what a signing slot names (slots take 14-digit PINFLs). */
export const certNationalId = (cert) => cert?.PINFL || cert?.pinfl || null;

/** A slot named by national ID takes a certificate with that ID; one named by account alone takes any. */
export function certFitsSlot(cert, p) {
  const id = participantNationalId(p);
  return !id || nationalIdsMayMatch(certNationalId(cert), id);
}

/**
 * The selected certificate may fill this slot: it is the caller's slot and
 * the certificate fits — or the certificate isn't registered with docs yet
 * (so the server can't know it is the caller's) and its ID matches the slot's.
 * Signing registers it first; the server decides then.
 */
export function slotTakesCert(p, cert, me) {
  if (!cert || !certFitsSlot(cert, p)) return false;
  if (participantIsMe(p, me)) return true;
  return !cert.isRegistered && Boolean(participantNationalId(p));
}
